// src/features/chat/components/RecordMessage.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useRecord } from '../../records/hooks/useRecord';
import styles from './RecordMessage.module.css';

const PREVIEW_LENGTH = 160;

const buildPreview = (body = '') => {
  const condensed = body.replace(/\s+/g, ' ').trim();
  if (!condensed) return '';
  if (condensed.length <= PREVIEW_LENGTH) {
    return condensed;
  }
  return `${condensed.slice(0, PREVIEW_LENGTH - 1).trimEnd()}…`;
};

const buildRelativeDate = (timestamp) => {
  if (!timestamp) return null;

  try {
    const asDate = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
    if (Number.isNaN(asDate.getTime())) return null;
    return formatDistanceToNow(asDate, { addSuffix: true });
  } catch (error) {
    console.warn('RecordMessage: could not format timestamp', error);
    return null;
  }
};

const RecordMessage = ({ recordId }) => {
  const { record, loading, recordError } = useRecord(recordId, {
    hydrate: 'none',
    realtime: true,
  });

  if (!recordId) {
    return null;
  }

  if (loading) {
    return (
      <div
        className={`${styles.recordMessage} ${styles.recordMessageLoading}`}
        aria-busy="true"
      >
        <div className={styles.recordMessageSkeletonIcon} />
        <div className={styles.recordMessageSkeletonContent}>
          <div className={styles.recordMessageSkeletonTitle} />
        </div>
      </div>
    );
  }

  const recordUnavailable = recordError?.message === 'Record not found' || record === null;

  if (recordUnavailable) {
    return (
      <div className={`${styles.recordMessage} ${styles.recordMessageError}`} role="alert">
        This record is no longer available.
      </div>
    );
  }

  if (recordError) {
    return (
      <div className={`${styles.recordMessage} ${styles.recordMessageError}`} role="alert">
        Unable to load this record right now.
      </div>
    );
  }

  const title = record.content?.title?.trim() || 'Untitled record';
  const preview = buildPreview(record.content?.body || '');
  const updatedRelative = buildRelativeDate(record.metadata?.updatedAt || record.metadata?.createdAt);

  return (
    <Link
      to={`/record/${recordId}`}
      className={styles.recordMessage}
      aria-label={`Open record ${title}`}
    >
      <div className={styles.recordMessageIcon}>
        <FileText size={20} />
      </div>
      <div className={styles.recordMessageContent}>
        <div className={styles.recordMessageTitle}>{title}</div>
        {preview && (
          <div className={styles.recordMessagePreview}>{preview}</div>
        )}
        {updatedRelative && (
          <div className={styles.recordMessageMeta}>Updated {updatedRelative}</div>
        )}
      </div>
    </Link>
  );
};

export default RecordMessage;
